import React, { useState } from 'react';
import axios from 'axios';
import {
  MapPin, Phone, Mail, MessageCircle, Facebook, Instagram, Youtube, Linkedin, Send, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = 'https://customer-assets.emergentagent.com/job_jilong-branch-id/artifacts/oh3wbs3f_LOGO-JILONG-INDONESI-white-150x58-1.png';

const scrollToId = (e, link) => {
  if (link.startsWith('#')) {
    e.preventDefault();
    const el = document.querySelector(link);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const Footer = () => {
  const { config, productCategories, supportItems } = useSiteContent();
  const [email, setEmail] = useState('');
  const [nlStatus, setNlStatus] = useState({ type: '', msg: '' });
  const [nlLoading, setNlLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setNlLoading(true);
    setNlStatus({ type: '', msg: '' });
    try {
      const res = await axios.post(`${API}/newsletter`, { email });
      if (res.data && res.data.id) {
        setNlStatus({ type: 'success', msg: 'Berhasil! Anda telah berlangganan newsletter.' });
        setEmail('');
      }
    } catch (err) {
      setNlStatus({
        type: 'error',
        msg: err.response?.data?.detail || 'Gagal berlangganan. Coba lagi.',
      });
    } finally {
      setNlLoading(false);
    }
  };

  const socials = [
    { Icon: Facebook, href: config.facebook || 'https://www.facebook.com/jilongcommunication', label: 'Facebook', hover: 'hover:bg-[#1877F2]' },
    { Icon: Instagram, href: config.instagram || 'https://www.instagram.com/jilong_communication/', label: 'Instagram', hover: 'hover:bg-gradient-to-br hover:from-[#833AB4] hover:via-[#E1306C] hover:to-[#FCAF45]' },
    { Icon: Youtube, href: config.youtube || 'https://www.youtube.com/@JilongCommunication', label: 'Youtube', hover: 'hover:bg-[#FF0000]' },
    { Icon: Linkedin, href: config.linkedin || 'https://www.linkedin.com/company/jilong-communication', label: 'LinkedIn', hover: 'hover:bg-[#0A66C2]' },
  ];

  const whatsapp = (config.whatsapp || '').replace(/[^0-9]/g, '');
  const privacy = config.privacy_url || 'https://jilongot.com/privacy.html';
  const terms = config.terms_url || 'https://jilongot.com/terms.html';

  return (
    <footer className="bg-[#0a1e3c] text-gray-300" data-testid="site-footer">
      <div className="bg-gradient-to-r from-[#0a3d8f] to-[#0a8f3d]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="text-2xl font-bold text-white font-[Montserrat]">Butuh konsultasi produk?</h3>
            <p className="text-blue-100 text-sm mt-1">Tim kami siap membantu menemukan solusi terbaik untuk jaringan optik Anda.</p>
          </div>
          <a
            href="#contact" onClick={(e) => scrollToId(e, '#contact')}
            data-testid="footer-cta-contact"
            className="bg-white text-[#0a3d8f] px-7 py-3 rounded-md font-bold hover:bg-blue-50 transition-colors shadow-lg whitespace-nowrap"
          >
            Hubungi Kami Sekarang
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <img src={LOGO} alt="Jilong Indonesia" className="h-12 w-auto mb-5" data-testid="footer-logo" />
            <p className="text-sm leading-relaxed text-gray-400">
              Cabang resmi JILONG Communication di Indonesia. Spesialis fusion splicer, OTDR, ODN dan kabel serat optik untuk industri telekomunikasi nasional.
            </p>
            <div className="flex items-center gap-3 mt-6">
              {socials.map(({ Icon, href, label, hover }, i) => (
                <a
                  key={i} href={href} target="_blank" rel="noopener noreferrer"
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className={`w-10 h-10 rounded-full bg-white/10 ${hover} text-white flex items-center justify-center transition-all duration-200 hover:scale-110 hover:shadow-lg cursor-pointer`}
                  aria-label={label}
                  title={label}
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Produk</h4>
            <ul className="space-y-2 text-sm">
              {productCategories
                .filter((c) => c.id !== 'odn' && c.id !== 'fiber-cables')
                .slice(0, 6)
                .map((c) => (
                <li key={c.id}>
                  <a
                    href={`/produk/${c.id}`}
                    className="hover:text-white transition-colors"
                    data-testid={`footer-product-${c.id}`}
                  >
                    {c.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Dukungan</h4>
            <ul className="space-y-2 text-sm">
              {supportItems.map((s) => (
                <li key={s.id}>
                  <a
                    href={s.link || '#support'}
                    target={s.link ? '_blank' : undefined}
                    rel="noreferrer"
                    className="hover:text-white transition-colors"
                    data-testid={`footer-support-${s.id}`}
                  >
                    {s.title}
                  </a>
                </li>
              ))}
              <li><a href="#about" onClick={(e) => scrollToId(e, '#about')} className="hover:text-white transition-colors">Tentang Jilong</a></li>
              <li><a href="#news" onClick={(e) => scrollToId(e, '#news')} className="hover:text-white transition-colors">Berita & Acara</a></li>
              <li><a href="/admin/login" className="hover:text-white transition-colors" data-testid="footer-admin-link">Admin Login</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Kontak</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="text-[#e60012] mt-0.5 shrink-0" />
                <span>{config.address}</span>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={16} className="text-[#e60012] mt-0.5 shrink-0" />
                <a href={`tel:${config.phone}`} className="hover:text-white">{config.phone}</a>
              </li>
              <li className="flex items-start gap-3">
                <MessageCircle size={16} className="text-[#0a8f3d] mt-0.5 shrink-0" />
                <a
                  href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer"
                  className="hover:text-white" data-testid="footer-whatsapp"
                >
                  WhatsApp {config.whatsapp}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={16} className="text-[#e60012] mt-0.5 shrink-0" />
                <a href={`mailto:${config.email}`} className="hover:text-white break-all">{config.email}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 grid lg:grid-cols-2 gap-6 items-center">
          <div>
            <h4 className="text-white font-bold text-lg mb-1 font-[Montserrat]">Berlangganan Newsletter</h4>
            <p className="text-sm text-gray-400">Dapatkan info produk terbaru, promo dan tips teknis langsung di email Anda.</p>
          </div>
          <form onSubmit={subscribe} data-testid="newsletter-form" className="flex flex-col gap-2">
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email" placeholder="email@perusahaan.com"
                className="flex-1 min-w-0 w-full px-4 py-2.5 rounded-md bg-white/5 border border-white/15 text-white placeholder-gray-500 focus:outline-none focus:border-[#0a8f3d] focus:ring-2 focus:ring-[#0a8f3d]/20 transition-colors"
              />
              <button
                type="submit" disabled={nlLoading} data-testid="newsletter-submit"
                className="inline-flex items-center gap-2 bg-[#0a8f3d] text-white px-5 py-2.5 rounded-md font-semibold hover:bg-[#087a33] transition-colors disabled:opacity-60 justify-center"
              >
                {nlLoading ? 'Mengirim...' : (<>Subscribe <Send size={14} /></>)}
              </button>
            </div>
            {nlStatus.msg && (
              <div
                data-testid="newsletter-status"
                className={`flex items-center gap-2 text-xs px-3 py-2 rounded-md ${nlStatus.type === 'success' ? 'bg-green-500/15 text-green-300 border border-green-500/30' : 'bg-red-500/15 text-red-300 border border-red-500/30'}`}
              >
                {nlStatus.type === 'success' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
                <span>{nlStatus.msg}</span>
              </div>
            )}
          </form>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-gray-500">
          <p>© {new Date().getFullYear()} JILONG Indonesia. All rights reserved.</p>
          <p>
            JILONG Communication — Authorized Indonesia Branch ·{' '}
            <a href={privacy} target="_blank" rel="noreferrer" className="hover:text-white" data-testid="footer-privacy">Privacy Policy</a> ·{' '}
            <a href={terms} target="_blank" rel="noreferrer" className="hover:text-white" data-testid="footer-terms">Terms</a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
